"use client";
import {
  __async,
  __spreadProps,
  __spreadValues
} from "../../../chunk-MMUBH76A.js";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { CpslButton, CpslIcon, CpslIdenticon, CpslText } from "@getpara/react-components";
import { useExternalWallets } from "../../providers/ExternalWalletContext.js";
import styled from "styled-components";
import { useThemeStore } from "../../stores/index.js";
import { truncateAddress, WalletType } from "@getpara/web-sdk";
import { useEffect, useRef } from "react";
import { useDropdownPosition } from "../AuthInput/hooks/useDropdownPosition.js";
import { useActiveWallet } from "../../hooks/useActiveWallet.js";
import { HeaderSelect, HeaderSelectContainer, HeaderSelectItem } from "../common.js";
import { useWalletState } from "../../../provider/index.js";
import { useInternalClient } from "../../../provider/hooks/utils/useInternalClient.js";
import { useCopyToClipboard } from "@getpara/react-common";
const getValue = (id, type) => {
  return id && type ? `${id}~${type}` : void 0;
};
const WALLET_TYPES = {
  [WalletType.EVM]: "EVM",
  [WalletType.SOLANA]: "Solana",
  [WalletType.COSMOS]: "Cosmos"
};
const ChainSelect = () => {
  var _a, _b;
  const containerRef = useRef(null);
  const { dropdownMaxHeight, dropdownWidth, mobileAnchor, resize } = useDropdownPosition(containerRef);
  const activeWallet = useActiveWallet();
  const { switchChain, chainId, chains, chainIdSwitchingTo } = useExternalWallets();
  useEffect(() => {
    if (dropdownMaxHeight && chainId) {
      resize();
    }
  }, [chainId, chainIdSwitchingTo, dropdownMaxHeight]);
  const handleChainChange = (chainId2) => __async(void 0, null, function* () {
    yield switchChain(chainId2);
  });
  if (!activeWallet || !activeWallet.isExternal || activeWallet.type === WalletType.SOLANA) {
    return null;
  }
  const chainIdToUse = chainIdSwitchingTo != null ? chainIdSwitchingTo : chainId;
  const selectedChainName = (_a = chains.find((c) => c.id.toString() === chainIdToUse)) == null ? void 0 : _a.name;
  return /* @__PURE__ */ jsx(Container, { children: /* @__PURE__ */ jsx(HeaderSelectContainer, { ref: containerRef, id: "inputContainer", children: /* @__PURE__ */ jsxs(
    HeaderSelect,
    {
      selectedValue: (_b = chainIdToUse == null ? void 0 : chainIdToUse.toString()) != null ? _b : "",
      onCpslSelectValueChange: (e) => {
        handleChainChange(e.detail);
      },
      showFormattedSelectedItem: true,
      placeholder: "Choose chain...",
      anchorElId: "inputContainer",
      dropdownMaxHeight,
      $width: dropdownWidth,
      $top: mobileAnchor + 16 + 1,
      autoWidth: true,
      selectedItemVariant: "bodyXS",
      children: [
        chainIdToUse && /* @__PURE__ */ jsx(ChainName, { variant: "bodyXS", color: "contrast", slot: "selected-item", children: selectedChainName }),
        chains == null ? void 0 : chains.map((chain) => /* @__PURE__ */ jsx(HeaderSelectItem, { slot: "items", value: chain.id.toString(), children: /* @__PURE__ */ jsx(ChainName, { variant: "bodyXS", color: "contrast", children: chain.name }) }, chain.id))
      ]
    }
  ) }) });
};
function getName(para, {
  type,
  isExternal,
  name,
  isMenu = false,
  hideWallets = false
}) {
  if (para.isMultiWallet) {
    return name != null ? name : `${isExternal ? "External " : ""}${WALLET_TYPES[type]}${!hideWallets && (isMenu || isExternal) ? " Wallet" : ""}`;
  }
  return hideWallets ? "My Account" : name || "My Wallet";
}
const AccountSelect = () => {
  var _a;
  const hideWallets = useThemeStore((state) => state.hideWallets);
  const para = useInternalClient();
  const containerRef = useRef(null);
  const { dropdownMaxHeight, dropdownWidth, mobileAnchor, resize } = useDropdownPosition(containerRef);
  const [isCopied, copy] = useCopyToClipboard();
  const { setSelectedWallet } = useWalletState();
  const activeWallet = useActiveWallet();
  const handleCopy = () => {
    copy(activeWallet.address ? para.getDisplayAddress(activeWallet.id, { addressType: activeWallet.type }) : "");
  };
  const ActiveWalletNode = activeWallet ? /* @__PURE__ */ jsxs(FlexRow, { slot: "selected-item", children: [
    /* @__PURE__ */ jsx(CpslIdenticon, { variant: "avatar", size: "24px", hash: para.getIdenticonHash(activeWallet.id, activeWallet.type) }),
    /* @__PURE__ */ jsx(WalletName, { variant: "bodyXS", color: "contrast", children: getName(para, __spreadProps(__spreadValues({}, activeWallet), { hideWallets })) }),
    !hideWallets && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(CpslText, { variant: "bodyXS", color: "secondary", children: (_a = activeWallet.ensName) != null ? _a : para.getDisplayAddress(activeWallet.id, { truncate: true, addressType: activeWallet.type }) }),
      /* @__PURE__ */ jsx(
        CpslButton,
        {
          id: "ignore-click",
          size: "small",
          variant: "ghost",
          onClick: (e) => {
            e.stopPropagation();
            e.preventDefault();
            handleCopy();
          },
          children: /* @__PURE__ */ jsx(CpslIcon, { id: "ignore-click", slot: "start", icon: isCopied ? "check" : "copy" })
        }
      )
    ] })
  ] }) : null;
  useEffect(() => {
    if (dropdownMaxHeight && (activeWallet == null ? void 0 : activeWallet.address)) {
      resize();
    }
  }, [activeWallet, para.availableWallets, dropdownMaxHeight]);
  return /* @__PURE__ */ jsx(Container, { children: /* @__PURE__ */ jsx(HeaderSelectContainer, { ref: containerRef, id: "addressInputContainer", children: para.availableWallets.length > 1 ? /* @__PURE__ */ jsxs(
    HeaderSelect,
    {
      selectedValue: getValue(activeWallet == null ? void 0 : activeWallet.id, activeWallet == null ? void 0 : activeWallet.type),
      onCpslSelectValueChange: (e) => {
        const [id, type] = e.detail.split("~");
        setSelectedWallet({ id, type });
      },
      showFormattedSelectedItem: true,
      placeholder: "Choose wallet...",
      anchorElId: "addressInputContainer",
      dropdownMaxHeight,
      $width: dropdownWidth,
      $top: mobileAnchor + 16 + 1,
      autoWidth: true,
      selectedItemVariant: "bodyXS",
      children: [
        activeWallet && ActiveWalletNode,
        para.availableWallets.map(({ address, name: _name, id, type, isExternal }) => {
          const key = getValue(id, type);
          const name = _name != null ? _name : getName(para, { type, isExternal, isMenu: true, hideWallets });
          return /* @__PURE__ */ jsx(HeaderSelectItem, { slot: "items", value: key, children: /* @__PURE__ */ jsxs(FlexRow, { children: [
            /* @__PURE__ */ jsx(CpslIdenticon, { size: "40px", hash: para.getIdenticonHash(id, type) }),
            /* @__PURE__ */ jsxs(FlexCol, { children: [
              name && /* @__PURE__ */ jsx(CpslText, { variant: "bodyS", color: "contrast", children: name }),
              !hideWallets && /* @__PURE__ */ jsx(CpslText, { variant: "bodyXS", color: "secondary", children: truncateAddress(address, type, { prefix: para.cosmosPrefix }) })
            ] })
          ] }) }, key);
        })
      ]
    }
  ) : ActiveWalletNode }) });
};
const Container = styled.div`
  flex: 0;
  width: 100%;
  display: flex;
  justify-content: center;
`;
const FlexRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const FlexCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;
const WalletName = styled(CpslText)`
  white-space: nowrap;
`;
const ChainName = styled(CpslText)`
  max-width: 150px;
  text-transform: capitalize;

  &::part(text-element) {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;
export {
  AccountSelect,
  ChainSelect
};
